/**
 * Capture the auth "landing type" from the URL the moment the app loads — BEFORE the
 * supabase-js client is constructed, because it clears `window.location.hash` during its
 * async init (detectSessionInUrl).
 *
 * Why this exists: a password-RESET link lands as `type=recovery`, which auth-js maps to
 * the PASSWORD_RECOVERY event (App.tsx already handles it). But an INVITE link
 * (inviteUserByEmail, #28 designer onboarding) lands as `type=invite` and auth-js emits a
 * plain SIGNED_IN — so without reading the type ourselves, an invited designer would drop
 * straight into the dashboard with NO password set. We read it here and route both
 * `invite` and `recovery` arrivals to the set-a-password screen (UpdatePassword), whose
 * `updateUser({ password })` works for either active session.
 *
 * This module is imported FIRST by src/lib/supabase.ts, so its top-level capture runs
 * before createClient() — i.e. before the hash is cleared. `parseAuthType` is pure so the
 * parsing is unit-tested.
 */

/** Pull the auth `type` from a URL hash + search (hash wins — implicit-flow lands there). */
export function parseAuthType(hash: string, search: string): string | null {
  const h = hash.startsWith('#') ? hash.slice(1) : hash;
  const fromHash = new URLSearchParams(h).get('type');
  if (fromHash) return fromHash;
  return new URLSearchParams(search).get('type');
}

/** True for the arrivals that should land on the set-a-password screen. */
export function isPasswordSetType(type: string | null): boolean {
  return type === 'invite' || type === 'recovery';
}

// Captured once, synchronously, at module load.
const landingType =
  typeof window === 'undefined'
    ? null
    : parseAuthType(window.location.hash, window.location.search);

/** Did the app load from an invite or password-reset link? */
export function isPasswordSetLanding(): boolean {
  return isPasswordSetType(landingType);
}
